/**
 * `useWorkflow(id)` — TanStack Query read hook for a single workflow.
 *
 * Implements the AC-1398 / AC-1377 P2.1 ticket and the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`, sections 2.4
 * (query key conventions), 5.2 (read-hook signatures), and 8.2 (Phase 2
 * verification table).
 *
 * ## Why this exists
 *
 * Today, `WorkflowEditor.tsx` calls `apiService.getWorkflow(id)` indirectly
 * through `useFlowStore.loadWorkflow`. That path has no caching, no request
 * deduplication, and is the reason we accumulated workarounds AC-1378
 * (`loadedWorkflowId` cache), AC-1381 (`allWorkflows` seed-from-list), and
 * AC-1386 (stale-fetch guard for A→B→A nav races). This hook is the canonical
 * read path going forward; the migration that removes the legacy store flow
 * lands in AC-1401 — see TD section 2.8.
 *
 * During this transitional phase the hook MUST coexist with
 * `flowStore.loadWorkflow`. Both paths can subscribe to the same workflow
 * concurrently — RQ's per-key request dedup means the duplicate fetch is
 * cheap. AC-1401 deletes the legacy path.
 *
 * ## Behavior
 *
 * - Fetches `apiService.getWorkflow(id)` with cache key `queryKeys.workflow(id)`.
 *   The factory coerces `'1'` and `1` to the same array shape, so route
 *   string IDs and store numeric IDs share a single cache slot. This is the
 *   AC-1396 coercion contract — see `queryKeys.ts` JSDoc.
 *
 * - `enabled` is auto-disabled for empty IDs (`undefined`, `null`, `''`).
 *   Without this guard, a `useQuery` whose key includes `Number(undefined)`
 *   (`NaN`) would cache under a useless key AND fire an HTTP request for an
 *   unknown ID. Today's call sites all guard with `if (!id) return` inside
 *   `useEffect`; this hook bakes that guard in.
 *
 * - Caller-provided `options` flow through to `useQuery`. Callers can override
 *   `staleTime`, `enabled`, `refetchOnMount`, etc. per-call without forking
 *   the hook. The caller's `enabled` is AND'ed with the internal id-truthy
 *   guard, so passing `{ enabled: false }` always wins.
 *
 * - Caller MUST NOT override `queryKey` or `queryFn` — those are owned by the
 *   hook (the whole point of centralizing the key factory is to prevent
 *   call-site key drift). The `Options` type below excludes them.
 *
 * ## Argument forwarded to `apiService.getWorkflow`
 *
 * The `id` value is passed through to `apiService.getWorkflow` as-is once
 * the truthy-id guard has cleared. `getWorkflow` URL-interpolates its
 * argument (`/api/v1/orchestrations/${workflowId}`), so both numeric and
 * string IDs serialize to the same URL. We deliberately do NOT pre-stringify
 * here so the call site argument matches what callers actually have in
 * scope (route params are strings, store IDs are numbers — both are valid
 * inputs and the cache key factory normalizes them under the hood).
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { Workflow } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Caller-overridable subset of `UseQueryOptions`. We intentionally exclude
 * `queryKey` and `queryFn` because they are owned by this hook — letting
 * callers override them defeats the centralization the migration depends on.
 */
export type UseWorkflowOptions = Omit<
  UseQueryOptions<Workflow, Error, Workflow, ReturnType<typeof queryKeys.workflow>>,
  'queryKey' | 'queryFn'
>;

/**
 * Returns the standard `UseQueryResult<Workflow>` from TanStack Query.
 *
 * @param id      Workflow ID. May be `string`, `number`, `undefined`, or
 *                `null`. Falsy values disable the query (no fetch fires).
 * @param options Optional `useQuery` options. `queryKey` and `queryFn` are
 *                excluded from this type because they are owned by the hook.
 *
 * @example
 *   const { data, isLoading, error } = useWorkflow(workflowId);
 *   if (isLoading) return <Spinner />;
 *   if (error) return <ErrorBanner error={error} />;
 *   return <Editor workflow={data!} />;
 *
 * @example
 *   // Defer the fetch until a user-action chooses an ID.
 *   const [pickedId, setPickedId] = useState<number | undefined>(undefined);
 *   const query = useWorkflow(pickedId);
 *   // Until pickedId becomes truthy, query.fetchStatus === 'idle' and no
 *   // HTTP request is made.
 */
export function useWorkflow(
  id: string | number | undefined | null,
  options?: UseWorkflowOptions,
): UseQueryResult<Workflow, Error> {
  // Treat `undefined`, `null`, and `''` as "no ID yet". Anything else is a
  // valid input — the queryKeys factory will coerce it to the canonical
  // numeric form. We pass the SAME canonical form to `apiService.getWorkflow`
  // so the cache key and the request URL agree.
  const hasId = id !== undefined && id !== null && id !== '';

  // The caller's `enabled` (if provided) is AND'ed with our internal
  // truthy-id guard. Either being falsy keeps the query disabled.
  const callerEnabled = options?.enabled;
  const enabled = hasId && (callerEnabled === undefined ? true : callerEnabled);

  return useQuery<Workflow, Error, Workflow, ReturnType<typeof queryKeys.workflow>>({
    // `as` cast: when `hasId` is false we still need to return SOME key shape
    // (RQ requires queryKey unconditionally), but `enabled: false` guarantees
    // the queryFn never runs and the key is never used as a cache slot we
    // care about. We use `0` as the placeholder ID so the cast is safe.
    queryKey: queryKeys.workflow(hasId ? (id as string | number) : 0),
    // Forward `id` as-is. `apiService.getWorkflow` URL-interpolates so both
    // numeric and string forms work; we use a runtime cast because TS's
    // current signature narrows to `string`, but the underlying call accepts
    // either. AC-1401's typing pass will widen `apiService.getWorkflow` to
    // `string | number` directly.
    queryFn: () => apiService.getWorkflow(id as string),
    ...options,
    enabled,
  });
}

export default useWorkflow;
